import React, { useContext, useEffect, useState } from "react";
import styles from "./ProdutoModal.module.scss";
import { Heart, Minus, Plus, ShoppingCart, X, Zap } from "lucide-react";
import { CartContext } from "shared/contexts/Cart/CartContext";
import { IProduto } from "shared/interfaces/IProduto";
import { AvaliacaoEstrelas } from "../avaliacaoestrelas/AvaliacaoEstrelas";
import { PrecoDisplay } from "../preco/PrecoDisplay";
import { SeletorVariacoes } from "./SeletorVariacoes";
import { useSelecaoCombinacao } from "./useSelecaoCombinacao";
import { Link, useNavigate } from "react-router-dom";
import { useFavoritos } from "shared/contexts/Auth/Favoritos/FavoritosContext";
import toast from "react-hot-toast";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import { formatarMoeda } from "shared/utils/money";
import { buscarAvaliacoesProduto, IAvaliacaoProduto } from "shared/services/api/compras.api";
import { useAuth } from "shared/contexts/Auth/AuthContext";

interface ProdutoModalProps {
  produto: IProduto;
}

export const ProdutoModal: React.FC<ProdutoModalProps> = ({ produto }) => {
  const { addItemCart } = useContext(CartContext);
  const navigate = useNavigate();
  const [quantidade, setQuantidade] = useState(1);
  const [imgAtiva, setImgAtiva] = useState(0);
  const [imagemExpandida, setImagemExpandida] = useState(false);
  const [avaliacoes, setAvaliacoes] = useState<IAvaliacaoProduto[]>([]);
  const [loadingAvaliacoes, setLoadingAvaliacoes] = useState(true);
  const [isDesktop, setIsDesktop] = useState(false);
  const { signed } = useAuth();

  const gallery = produto.gallery && produto.gallery.length > 0
    ? produto.gallery
    : ["/imagens/default.png"];

  const selecaoCombinacao = useSelecaoCombinacao(produto);
  const { preco, aPartirDe, parcelamento, estoque, podeComprar, combinacao } = selecaoCombinacao;
  const total = preco * quantidade;

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1024px)");
    const atualizarTela = () => setIsDesktop(mediaQuery.matches);
    atualizarTela();
    mediaQuery.addEventListener("change", atualizarTela);
    return () => mediaQuery.removeEventListener("change", atualizarTela);
  }, []);

  useEffect(() => {
    setQuantidade(1);
  }, [combinacao?.id]);

  const { favoritesIds, addFavorite, removeFavorite } = useFavoritos();
  const isFavorito = favoritesIds.includes(produto.id);

  useEffect(() => {
    let ativo = true;

    async function carregarAvaliacoes() {
      if (!produto.id) {
        setAvaliacoes([]);
        setLoadingAvaliacoes(false);
        return;
      }

      try {
        setLoadingAvaliacoes(true);
        const resultado = await buscarAvaliacoesProduto(produto.id, 1, 50);
        if (ativo) setAvaliacoes(resultado);
      } catch (error) {
        if (ativo) setAvaliacoes([]);
      } finally {
        if (ativo) setLoadingAvaliacoes(false);
      }
    }

    carregarAvaliacoes();
    return () => {
      ativo = false;
    };
  }, [produto.id]);

  const mediaAvaliacao = avaliacoes.length > 0
    ? avaliacoes.reduce((total, avaliacao) => total + Number(avaliacao.rating || 0), 0) /
      avaliacoes.length
    : 0;

  const quantidadeAvaliacoes = avaliacoes.length;

  const caracteristicasFormatadas = (produto.caracteristicas ?? []).map((caracteristica: string) =>
    caracteristica.replace(/([a-záéíóúãõç])([A-ZÁÉÍÓÚÃÕÇ])/g, "$1 $2").trim()
  );

  const toggleFavorite = async () => {
    if (!signed) {
      toast.error("Faça login para adicionar produtos aos favoritos.");
      return;
    }

    try {
      if (isFavorito) {
        await removeFavorite(produto.id);
        toast.success("Removido dos favoritos");
      } else {
        await addFavorite(produto.id);
        toast.success("Adicionado aos favoritos");
      }
    } catch (error) {
      toast.error(isFavorito
        ? "Não foi possível remover dos favoritos. Tente novamente."
        : "Não foi possível adicionar aos favoritos. Tente novamente."
      );
    }
  };

  const limiteAtingido = quantidade >= (estoque ?? 0);

  const aumentar = () => {
    if (!limiteAtingido) setQuantidade((prev) => prev + 1);
  };

  const diminuir = () => setQuantidade((prev) => (prev > 1 ? prev - 1 : prev));

  const handleComprarAgora = () => {
    if (!signed) {
      toast.error("Faça login para comprar este produto.");
      return;
    }

    if (!podeComprar) return;

    addItemCart(produto, quantidade, combinacao?.id ?? null);
    navigate("/carrinho");
  };

  const handleAddCart = () => {
    if (!signed) {
      toast.error("Faça login para adicionar produtos ao carrinho.");
      return;
    }

    if (!podeComprar) return;

    addItemCart(produto, quantidade, combinacao?.id ?? null);
    toast.success("Produto adicionado ao carrinho");
  };

  const textoBotaoCompra = (texto: string) => {
    if (podeComprar) return texto;
    if (produto.hasVariations && !combinacao) return "Escolha as opções";
    return "Esgotado";
  };

  const handleToggleFavorite = () => toggleFavorite();
  const selecionarImagem = (index: number) => setImgAtiva(index);

  return (
    <div className={styles.page}>
      <div className={styles.layout}>
        <div className={styles.galleryColumn}>
          <div className={styles.imageArea}>
            {gallery.length > 1 && (
              <div className={styles.miniaturasContainer}>
                <Swiper
                  direction={isDesktop ? "vertical" : "horizontal"}
                  slidesPerView={isDesktop ? 4 : 3}
                  spaceBetween={10}
                  grabCursor
                  className={styles.miniaturasSwiper}
                >
                  {gallery.map((image, index) => (
                    <SwiperSlide key={`${image}-${index}`}>
                      <button
                        type="button"
                        className={`${styles.miniatura} ${
                          imgAtiva === index ? styles.miniaturaAtiva : ""
                        }`}
                        onClick={() => selecionarImagem(index)}
                      >
                        <img src={image} alt={`${produto.name} ${index + 1}`} />
                      </button>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            )}

            <div className={styles.imageWrapper}>
              <button
                type="button"
                className={styles.imageButton}
                onClick={() => setImagemExpandida(true)}
              >
                <img src={gallery[imgAtiva]} alt={produto.name} />
              </button>

              <div className={styles.actions}>
                <button type="button" onClick={handleAddCart} aria-label="Adicionar ao carrinho">
                  <ShoppingCart size={18} color="#F07B2E" />
                </button>
                <button type="button" onClick={handleToggleFavorite} aria-label="Adicionar aos favoritos">
                  <Heart
                    size={18}
                    color="#F07B2E"
                    fill={isFavorito ? "#F07B2E" : "transparent"}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.content}>
          <div className={styles.containerNome}>
            <h1 className={styles.nome}>{produto.name}</h1>

            {/* lojaNome vem do storeName de GET /v2/products/:id */}
            {produto.lojaNome && produto.lojaId && (
              <p className={styles.vendidoPor}>
                Vendido por:{" "}
                <Link
                  to={`/lojas/${produto.lojaId}`}
                  className={styles.linkLoja}
                >
                  {produto.lojaNome}
                </Link>
              </p>
            )}

            <div className={styles.containerAvaliacoes}>
              {loadingAvaliacoes ? (
                <>
                  <AvaliacaoEstrelas rating={0} />
                  <p>0.0</p>
                  <p>(0 avaliações)</p>
                </>
              ) : (
                <>
                  <AvaliacaoEstrelas rating={mediaAvaliacao} />
                  <p>{mediaAvaliacao.toFixed(1)}</p>
                  <p>
                    ({quantidadeAvaliacoes}{" "}
                    {quantidadeAvaliacoes === 1 ? "avaliação" : "avaliações"})
                  </p>
                </>
              )}
            </div>

            {estoque !== null && (
              <p className={styles.estoque}>
                {estoque > 0 ? `${estoque} unidades disponíveis` : "Esgotado"}
              </p>
            )}
          </div>

          <div className={styles.precoContainer}>
            <p className={styles.preco}>
              {aPartirDe && "A partir de "}
              <PrecoDisplay valor={preco} />
            </p>

            {parcelamento && (
              <p className={styles.parcelamento}>
                Em até {parcelamento.installments}x de{" "}
                <span>{formatarMoeda(parcelamento.installmentAmount)}</span> sem juros
              </p>
            )}
          </div>

          {produto.hasVariations && (
            <SeletorVariacoes variacoes={produto.variations ?? []} selecao={selecaoCombinacao} />
          )}

          <div className={styles.containerDescricao}>
            <h2 className={styles.Titulo}>Descrição</h2>
            <p className={styles.descricao}>{produto.descricao}</p>

            {caracteristicasFormatadas.length > 0 && (
              <div className={styles.caracteristicas}>
                {caracteristicasFormatadas.map((caracteristica, index) => (
                  <div key={`${caracteristica}-${index}`} className={styles.caracteristicaItem}>
                    <span className={styles.caracteristicaBullet}>•</span>
                    <span>{caracteristica}</span>
                  </div>
                ))}
              </div>
            )}

            <div className={styles.doisBlocos}>
              <div>
                <p className={styles.label}>Categoria</p>
                <p className={styles.categoria}>{produto.categoriaNome}</p>
              </div>
              <div>
                <p className={styles.label}>Entrega</p>
                <p className={styles.entrega}>{produto.entrega || "Em até 1h"}</p>
              </div>
            </div>
          </div>

          <div className={styles.containerQtd}>
            <div className={styles.qtd}>
              <Minus
                onClick={diminuir}
                size={20}
                className={styles.qtsIconRight}
              />
              <span>{quantidade}</span>
              <Plus
                onClick={aumentar}
                size={20}
                className={`${styles.qtsIconLeft} ${
                  limiteAtingido ? styles.qtdDesabilitada : ""
                }`}
              />
            </div>

            <div className={styles.totalBox}>
              <p>Total</p>
              <strong><PrecoDisplay valor={total} /></strong>
            </div>
          </div>

          <div className={styles.buttons}>
            <button className={styles.btnComprar} onClick={handleComprarAgora} disabled={!podeComprar}>
              <Zap size={20} />
              {textoBotaoCompra("Comprar Agora")}
            </button>
            <button className={styles.btnCarrinho} onClick={handleAddCart} disabled={!podeComprar}>
              <ShoppingCart size={20} />
              {textoBotaoCompra("Adicionar ao Carrinho")}
            </button>
          </div>
        </div>
      </div>

      {imagemExpandida && (
        <div className={styles.lightbox} onClick={() => setImagemExpandida(false)}>
          <button
            type="button"
            className={styles.btnLightboxClose}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setImagemExpandida(false);
            }}
            aria-label="Fechar imagem"
          >
            <X size={18} />
          </button>

          <Swiper
            slidesPerView={1}
            spaceBetween={0}
            grabCursor
            initialSlide={imgAtiva}
            onSlideChange={(instance: SwiperType) => setImgAtiva(instance.activeIndex)}
            className={styles.lightboxSwiper}
          >
            {gallery.map((image, index) => (
              <SwiperSlide key={`expandida-${image}-${index}`}>
                <div className={styles.lightboxSlide} onClick={(e) => e.stopPropagation()}>
                  <img src={image} alt={`${produto.name} ${index + 1}`} />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {gallery.length > 1 && (
            <div className={styles.lightboxDots}>
              {gallery.map((_, index) => (
                <span key={index} className={imgAtiva === index ? styles.active : ""} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};